import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Phone, ClipboardList, CheckCircle, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ServiceGallery } from '../components/ServiceGallery';

export const MetalRoofInstallationPage = () => {
  useEffect(() => {
    // SEO: Set page title and meta description
    document.title = "Metal Roof Installation Sydney | Professional Roofing Services | Dezire Metal Roofing";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional metal roof installation services in Greater Sydney. New builds, renovations, and upgrades with quality materials. Book a free inspection today.');
    }
    window.scrollTo(0, 0);
  }, []);

  const services = [
    "New metal roof installation",
    "Re-roofing and upgrades",
    "Residential roofing solutions",
    "Flashings and finishing details",
    "Guttering and drainage integration"
  ];

  const benefits = [
    "Durability - Metal roofing lasts 40-70 years",
    "Low maintenance requirements",
    "Reliable protection against harsh weather",
    "Improved appearance and street appeal",
    "Long-term value for your property"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="page-title">
              Metal Roof Installation <span className="text-primary">Sydney</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Dezire Metal Roofing provides professional metal roof installation services for homeowners across Greater Sydney. Whether you are building new, renovating, or upgrading an older roof, we install metal roofing systems designed for durability, performance, and a clean modern finish.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-inspection">
                <Button size="lg" className="bg-primary hover:bg-accent text-white" data-testid="hero-cta-btn">
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Book a Free Inspection
                </Button>
              </Link>
              <a href="/call">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 0433 973 844
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We work with quality materials suited to Australian weather conditions and focus on professional workmanship at every stage of the job.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Why Choose Metal Roofing?
              </h2>
              <p className="text-gray-600 mb-6">
                Metal roofing is a strong choice for Sydney homes because it offers durability, low maintenance, and reliable protection against harsh weather. It can also improve the appearance of your property while delivering long-term value.
              </p>

              <ul className="space-y-3 mb-8">
                {benefits.map((benefit, index) => (
                  <li key={`benefit-${index}-${benefit.slice(0, 10)}`} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-gray-50 p-8 rounded-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  What We Offer
                </h2>
                <ul className="space-y-4">
                  {services.map((service, index) => (
                    <li key={`service-${index}-${service.slice(0, 10)}`} className="flex items-center">
                      <div className="w-2 h-2 bg-primary rounded-full mr-4"></div>
                      <span className="text-gray-700 text-lg">{service}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <p className="text-gray-600 mb-6">
                    Contact Dezire Metal Roofing today to book a free inspection and quote for your metal roofing project.
                  </p>
                  <Link to="/book-inspection">
                    <Button className="w-full bg-primary hover:bg-accent text-white" data-testid="content-cta-btn">
                      Get a Free Quote
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <ServiceGallery service="metal-roof-installation" />

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Related Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/services/roof-repairs" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Roof Repairs</h3>
              <p className="text-gray-600 text-sm">Fast repairs for leaks and damage</p>
            </Link>
            <Link to="/services/roof-replacement" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Roof Replacement</h3>
              <p className="text-gray-600 text-sm">Complete roof replacement solutions</p>
            </Link>
            <Link to="/services/gutter-installation" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Gutter Installation</h3>
              <p className="text-gray-600 text-sm">Gutters and drainage systems</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MetalRoofInstallationPage;
